(() => {
  const REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');
  const revealObserver = !REDUCED_MOTION.matches && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-pmx-revealed');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: .12 })
    : null;

  const prepareReveals = (root = document) => {
    const targets = [
      ...(root.matches?.('[data-pmx-reveal]') ? [root] : []),
      ...root.querySelectorAll('[data-pmx-reveal]'),
    ];
    if (!targets.length) return;
    document.documentElement.classList.add('pmx-motion-ready');
    targets.forEach((target) => {
      const group = target.closest('[data-pmx-reveal-group]');
      const siblings = group ? [...group.querySelectorAll('[data-pmx-reveal]')] : [];
      const index = Math.max(0, siblings.indexOf(target));
      target.style.setProperty('--pmx-reveal-delay', `${Math.min(index * 90, 360)}ms`);
      if (revealObserver) revealObserver.observe(target);
      else target.classList.add('is-pmx-revealed');
    });
  };

  const hash = (value) => {
    let result = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      result ^= value.charCodeAt(index);
      result = Math.imul(result, 16777619);
    }
    return Math.abs(result);
  };

  const initField = (section, state) => {
    const canvas = section.querySelector('[data-passage-field]');
    const gl = canvas?.getContext('webgl', { alpha: true, antialias: true, powerPreference: 'low-power' });
    if (!gl) {
      canvas.hidden = true;
      return { draw: () => {}, destroy: () => {} };
    }

    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
    };

    const vertex = compile(gl.VERTEX_SHADER, `
      attribute vec2 position;
      attribute vec4 color;
      attribute float size;
      varying vec4 pointColor;
      uniform float pulse;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
        gl_PointSize = size + pulse;
        pointColor = color;
      }
    `);
    const fragment = compile(gl.FRAGMENT_SHADER, `
      precision mediump float;
      varying vec4 pointColor;
      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float radius = length(center);
        if (radius > 0.5) discard;
        gl_FragColor = vec4(pointColor.rgb, pointColor.a * smoothstep(0.5, 0.08, radius));
      }
    `);

    if (!vertex || !fragment) {
      canvas.hidden = true;
      return { draw: () => {}, destroy: () => {} };
    }

    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      canvas.hidden = true;
      return { draw: () => {}, destroy: () => {} };
    }

    const locations = {
      position: gl.getAttribLocation(program, 'position'),
      color: gl.getAttribLocation(program, 'color'),
      size: gl.getAttribLocation(program, 'size'),
      pulse: gl.getUniformLocation(program, 'pulse'),
    };
    const buffer = gl.createBuffer();
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    let last = 0;
    let points = [];

    const colors = {
      feed: [.78, .72, .62, 1],
      treat: [.95, .54, .18, 1],
      chew: [.54, .55, .39, 1],
      play: [.3, .58, .78, 1],
      care: [.97, .96, .94, 1],
      groom: [.78, .72, .62, 1],
    };

    const resize = () => {
      const ratio = Math.min(devicePixelRatio || 1, 1.5);
      const width = Math.max(1, Math.floor(canvas.clientWidth * ratio));
      const height = Math.max(1, Math.floor(canvas.clientHeight * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    };

    const animate = (stamp = 0) => {
      resize();
      const delta = Math.min(32, Math.max(0, stamp - last) || 16);
      last = stamp;
      const data = [];
      points.forEach((point) => {
        if (!reduced) {
          point.x -= point.speed * delta;
          if (point.x < -.08) point.x = 1.08;
        }
        data.push(point.x * 2 - 1, 1 - point.y * 2, ...point.color, point.size);
      });

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.DYNAMIC_DRAW);
      const stride = 7 * Float32Array.BYTES_PER_ELEMENT;
      gl.enableVertexAttribArray(locations.position);
      gl.vertexAttribPointer(locations.position, 2, gl.FLOAT, false, stride, 0);
      gl.enableVertexAttribArray(locations.color);
      gl.vertexAttribPointer(locations.color, 4, gl.FLOAT, false, stride, 2 * Float32Array.BYTES_PER_ELEMENT);
      gl.enableVertexAttribArray(locations.size);
      gl.vertexAttribPointer(locations.size, 1, gl.FLOAT, false, stride, 6 * Float32Array.BYTES_PER_ELEMENT);
      gl.uniform1f(locations.pulse, reduced ? 0 : (Math.sin(stamp / 180) + 1));
      gl.drawArrays(gl.POINTS, 0, points.length);
      if (!reduced) frame = requestAnimationFrame(animate);
    };

    return {
      draw(products) {
        cancelAnimationFrame(frame);
        points = products.map((product) => {
          const seed = hash(product.search);
          const inventoryRatio = Math.min(1, Math.log10(product.inventory + 1) / 4);
          return {
            x: .08 + ((seed % 1000) / 1000) * 1.04,
            y: ['feed', 'treat', 'chew'].includes(product.need) ? .28 : .7,
            speed: .000018 + (product.price / Math.max(1, ...products.map((item) => item.price))) * .000018,
            size: 12 + inventoryRatio * 22,
            color: colors[product.need] || colors.care,
          };
        });
        last = performance.now();
        frame = requestAnimationFrame(animate);
      },
      destroy() {
        cancelAnimationFrame(frame);
      },
    };
  };

  const init = (section) => {
    if (section.dataset.passageReady === 'true') return;
    section.dataset.passageReady = 'true';
    section.classList.add('is-enhanced');

    const cards = [...section.querySelectorAll('[data-passage-product]')];
    const products = cards.map((card) => ({
      card,
      species: card.dataset.species,
      need: card.dataset.need,
      price: Number(card.dataset.price) || 0,
      inventory: Number(card.dataset.inventory) || 0,
      search: card.dataset.search || '',
    }));
    const state = { mode: 'dog', need: 'all', query: '' };
    const field = initField(section, state);
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const modeButtons = [...section.querySelectorAll('[data-passage-mode]')];
    let autoplayTimer = 0;

    const animateModeChange = () => {
      const copy = section.querySelector('.pmx-passage__copy');
      const mark = section.querySelector('.pmx-passage__mark');
      if (reducedMotion || !copy?.animate) return;
      copy.animate(
        [
          { opacity: .36, transform: 'translate3d(0, 18px, 0)' },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' },
        ],
        { duration: 380, easing: 'cubic-bezier(.16, 1, .3, 1)' },
      );
      mark?.animate(
        [
          { opacity: .32, filter: 'blur(7px)' },
          { opacity: 1, filter: 'blur(0)' },
        ],
        { duration: 560, easing: 'cubic-bezier(.16, 1, .3, 1)' },
      );
    };

    const render = () => {
      const grooming = state.mode === 'grooming';
      const activeAnimal = section.querySelector(`.pmx-passage__animal--${state.mode}`);
      if (activeAnimal && !activeAnimal.src && activeAnimal.dataset.passageSrc) {
        activeAnimal.src = activeAnimal.dataset.passageSrc;
      }
      const eligible = products.filter((product) => {
        const speciesMatch = product.species === state.mode;
        const needMatch = grooming || state.need === 'all' || product.need === state.need;
        return speciesMatch && needMatch && (!state.query || product.search.includes(state.query));
      });
      const prioritized = [...eligible]
        .sort((left, right) => right.inventory - left.inventory)
        .slice(0, innerWidth <= 1024 ? 6 : 5);
      const prices = eligible.map((product) => product.price);
      const maxPrice = Math.max(1, ...prices);

      cards.forEach((card) => card.classList.remove('is-in-passage'));
      prioritized.forEach((product, index) => {
        const phase = (index + .5) / prioritized.length;
        const inventoryRatio = Math.min(1, Math.log10(product.inventory + 1) / 4);
        const duration = 18 + (product.price / maxPrice) * 10;
        product.card.style.setProperty('--passage-lane', ['feed', 'treat', 'chew'].includes(product.need) ? '14%' : '54%');
        product.card.style.setProperty('--passage-duration', `${duration.toFixed(2)}s`);
        product.card.style.setProperty('--passage-delay', `${(-phase * duration).toFixed(2)}s`);
        product.card.style.setProperty('--passage-scale', (.92 + inventoryRatio * .08).toFixed(3));
        product.card.style.setProperty('--passage-drift', `${index % 2 === 0 ? -6 : 6}px`);
        product.card.style.setProperty('--passage-static-x', `${12 + (index % 5) * 17}%`);
        product.card.classList.add('is-in-passage');
      });

      section.dataset.mode = state.mode;
      section.querySelector('[data-passage-subject]').textContent = state.mode === 'cat'
        ? section.dataset.catSubject
        : section.dataset.dogSubject;
      section.querySelector('[data-passage-noun]').textContent = grooming ? section.dataset.groomingHeading : section.dataset.picksLabel;
      section.querySelector('[data-passage-subline]').textContent = grooming ? section.dataset.groomingSubline : section.dataset.passageLabel;
      const configured = {
        dog: section.dataset.dogText,
        cat: section.dataset.catText,
        grooming: section.dataset.groomingText,
      };
      const lede = section.querySelector('[data-passage-lede]');
      if (configured[state.mode]) lede.textContent = configured[state.mode];
      section.querySelector('[data-passage-empty]').hidden = eligible.length > 0;
      section.querySelectorAll('[data-passage-need]').forEach((button) => {
        const available = button.dataset.passageNeed === 'all'
          || products.some((product) => product.species === state.mode && product.need === button.dataset.passageNeed);
        button.disabled = !available;
        button.hidden = !available;
        button.setAttribute('aria-disabled', String(!available));
      });
      const availableFilterCount = section.querySelectorAll('[data-passage-need]:not([hidden])').length;
      section.style.setProperty('--passage-filter-count', String(Math.max(1, availableFilterCount)));
      field.draw(eligible);
    };

    /*
     * Autoplay yields to the search box.
     *
     * Grooming mode hides the search form in CSS, so an unattended rotation
     * used to pull the field out from under anyone typing in it — and, once
     * suggestions existed, close a panel they were still reading. While the
     * field holds focus or text, the chapter timer waits its turn instead.
     */
    const searchEngaged = () => {
      const queryField = section.querySelector('[data-passage-query]');
      return !!queryField && (queryField.value.trim() !== '' || document.activeElement === queryField);
    };

    const scheduleAutoplay = () => {
      clearTimeout(autoplayTimer);
      if (reducedMotion) return;
      autoplayTimer = setTimeout(() => {
        if (searchEngaged()) {
          scheduleAutoplay();
          return;
        }
        const current = modeButtons.findIndex((button) => button.dataset.passageMode === state.mode);
        modeButtons[(current + 1) % modeButtons.length].click();
      }, 7000);
    };

    modeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        state.mode = button.dataset.passageMode;
        state.need = state.mode === 'grooming' ? 'groom' : 'all';
        // Only a real click resets what the shopper typed. The autoplay timer
        // changes chapter and leaves the search box exactly as it found it.
        if (event.isTrusted) {
          state.query = '';
          section.querySelector('[data-passage-query]').value = '';
          section.passageCloseSuggestions?.();
        }
        modeButtons.forEach((candidate) => {
          candidate.setAttribute('aria-pressed', String(candidate === button));
        });
        section.querySelectorAll('[data-passage-need]').forEach((candidate) => {
          candidate.setAttribute('aria-pressed', String(candidate.dataset.passageNeed === state.need));
        });
        render();
        animateModeChange();
        if (event.isTrusted) clearTimeout(autoplayTimer);
        else scheduleAutoplay();
      });
    });

    section.querySelectorAll('[data-passage-need]').forEach((button) => {
      button.addEventListener('click', () => {
        state.need = button.dataset.passageNeed;
        section.querySelectorAll('[data-passage-need]').forEach((candidate) => {
          candidate.setAttribute('aria-pressed', String(candidate === button));
        });
        render();
      });
    });

    /*
     * Live suggestions.
     *
     * Typing filters the rendered product stream — instant, no network. The
     * suggestion panel on top of it is the shared PMX search behaviour, the
     * same one the global header uses: predictive search, then the catalogue
     * endpoint, then spelling correction, then an honest empty state. If
     * pmx-search.js fails to load the form is still a plain GET to /search.
     */
    const searchForm = section.querySelector('[data-passage-search]');
    const searchInput = section.querySelector('[data-passage-query]');
    const suggestions = section.querySelector('[data-passage-suggestions]');
    const searchListeners = new AbortController();
    const searchSignal = searchListeners.signal;

    const search = window.PMXSearch?.attach({
      form: searchForm,
      input: searchInput,
      panel: suggestions,
      status: section.querySelector('[data-passage-search-status]'),
      predictiveEndpoint: searchForm?.dataset.predictiveEndpoint,
      predictiveSection: searchForm?.dataset.predictiveSection,
      semanticEndpoint: searchForm?.dataset.semanticEndpoint,
      semanticLabel: searchForm?.dataset.semanticLabel,
    });

    // Product filtering is the passage's own job and stays local to it.
    searchInput?.addEventListener('input', (event) => {
      state.query = event.currentTarget.value.trim().toLowerCase();
      render();
    }, { signal: searchSignal });

    section.passageCloseSuggestions = () => search?.close();

    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(render, 100);
    };
    addEventListener('resize', onResize, { passive: true });
    section.passageDestroy = () => {
      removeEventListener('resize', onResize);
      clearTimeout(autoplayTimer);
      searchListeners.abort();
      search?.destroy();
      field.destroy();
    };
    render();
    scheduleAutoplay();
  };

  const boot = (root = document) => {
    root.querySelectorAll('[data-pmx-passage]').forEach(init);
    prepareReveals(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-pmx-passage]')?.passageDestroy?.();
    event.target.querySelectorAll('[data-pmx-reveal]').forEach((target) => revealObserver?.unobserve(target));
  });
})();
